import { FC, memo, useEffect, useState } from "react";
import { CodeBlock } from "../markdown/code-block";
import { MemoizedReactMarkdown } from "../markdown/memoized-react-markdown";
import rehypeKatex from "rehype-katex";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import "katex/dist/katex.min.css";
import { Message } from "../../interfaces/chat.interface";
import { CopyIcon, ThumbsDownIcon, ThumbsUpIcon, User } from "lucide-react";
import { Citation } from "@/constants/citation.constant";
import { useCitation } from "@/hooks/use-citation";
import { CitationButton } from "../citation/citationButton";
import { RightSideModal } from "../modal/rightSideModal";
import aiIcon from "@/assets/ai-logo.svg";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { FileAttachment } from "./attachment";
import MessageFeedbackButton from "./message-feedback";
import { ProfileIcon } from "../profile/profileIcon";
import AssistantMessage from "./assistantMessage";
import { FileAttachmentWrapper } from "./attachement-wrapper";

/**
 * Process content to make citation numbers clickable
 * Replaces [1], [2], etc. with clickable elements
 */
const processClickableCitations = (
  content: string,
  onCitationClick: (citationNumber: number) => void
): string => {
  return content.replace(/\[Ref\s+(\d+)\]/gi, (_match, citationNumber) => {
    return `<span class="citation-number" data-citation="${citationNumber}" style="cursor: pointer; color: #2563eb; font-weight: 500; text-decoration: underline;">[${citationNumber}]</span>`;
  });
};

/**
 * Parse citation sources from streaming text content
 * Extracts sources from the **Sources:** section at the end of the response
 */
const parseSourcesFromContent = (content: string) => {
  try {
    // Look for the Sources section at the end of the content
    const sourcesIndex = content.lastIndexOf("**Sources:**");
    if (sourcesIndex === -1) {
      return [];
    }

    // Extract everything after the last **Sources:** occurrence
    const sourcesText = content.substring(sourcesIndex);
    let sources: any = [];

    // Look for **[Doc X]** patterns in the sources text
    const docMatches = sourcesText.match(
      /\*\*\[Doc (\d+)\]\*\*\s*(.+?)(?:\n|$)/g
    );
    if (!docMatches) {
      return [];
    }

    docMatches.forEach((match, index) => {
      const docMatch = match.match(/\*\*\[Doc (\d+)\]\*\*\s*(.+?)(?:\n|$)/);
      if (!docMatch) return;

      const docId = docMatch[1];
      const title = docMatch[2].trim();

      // Find the corresponding source and URL for this document
      const docStartIndex = sourcesText.indexOf(match);
      const nextDocIndex = docMatches[index + 1]
        ? sourcesText.indexOf(docMatches[index + 1])
        : sourcesText.length;
      const docSection = sourcesText.substring(docStartIndex, nextDocIndex);

      const sourceMatch = docSection.match(/- Source:\s*(.+?)(?:\n|$)/);
      const urlMatch = docSection.match(/- \[View Document\]\((.+?)\)/);

      const source = sourceMatch ? sourceMatch[1].trim() : "Unknown Source";
      const url = urlMatch ? urlMatch[1].trim() : "";

      // Extract actual document title from URL
      let actualTitle = title;
      if (url) {
        const urlParts = url.split("/");
        const fileName = urlParts[urlParts.length - 1];
        if (fileName && fileName !== "") {
          // Remove file extension and decode URL
          const decodedFileName = decodeURIComponent(fileName);
          const titleFromUrl = decodedFileName.replace(
            /\.(docx|pdf|doc)$/i,
            ""
          );
          if (titleFromUrl && titleFromUrl !== "") {
            actualTitle = titleFromUrl;
          }
        }
      }

      sources.push({
        id: parseInt(docId, 10),
        name: actualTitle,
        content: `${actualTitle}\nSource: ${source}\nURL: ${url}`,
        url: url,
        parentFolder: source,
      });
    });

    return sources;
  } catch (error) {
    return [];
  }
};

/**
 * Props for the ChatMessage component
 */
interface Props {
  /** Message data to display */
  message: Message;
  /** Index of the message in the conversation */
  messageIndex: number;
  /** Callback function called when sources are opened */
  onOpenSources?: (sources: Citation[]) => void;
  /** Callback function called when sources are closed */
  onCloseSources?: () => void;
  /** Whether the sources modal is currently open */
  sourcesOpen?: boolean;
  /** Whether the message is currently streaming */
  isStreaming?: boolean;
}

/**
 * ChatMessage component displays a single chat message (user or assistant).
 * Handles markdown rendering, citations, file attachments, feedback, copy functionality,
 * and reasoning display. Supports clickable citation numbers and source parsing.
 * 
 * @component
 * @param {Props} props - Component props
 * @param {Message} props.message - Message data to display
 * @param {number} props.messageIndex - Message index in conversation
 * @param {(sources: Citation[]) => void} [props.onOpenSources] - Callback fired when sources opened
 * @param {boolean} [props.sourcesOpen=false] - Whether sources modal is open
 * @param {boolean} [props.isStreaming=false] - Whether message is streaming
 * @returns {JSX.Element} Chat message with avatar, content, and actions
 * 
 * @example
 * ```tsx
 * <ChatMessage 
 *   message={message}
 *   messageIndex={0}
 *   onOpenSources={handleOpenSources}
 *   isStreaming={false}
 * />
 * ```
 */
export const ChatMessage: FC<Props> = memo(
  ({
    message,
    messageIndex,
    onOpenSources,
    onCloseSources,
    sourcesOpen,
    isStreaming = false,
  }) => {
    const [citationMessage, setCitationMessage] = useState<string>();
    const [citationSource, setCitationSource] = useState<Citation[]>([]);

    const handleCitationClick = (citationNumber: number) => {
      if (citationSource && citationSource.length > 0 && onOpenSources) {
        onOpenSources(citationSource);
        const targetCitation = citationSource[citationNumber - 1];
        if (targetCitation) {
          setTimeout(() => {
            if (window.openCitationDetails) {
              window.openCitationDetails(targetCitation);
            }
          }, 100);
        }
      }
    };
    const { open, reference, isLoading, error, openCitation, close } =
      useCitation();

    const COPY_TOOLTIP_DURATION = 2000;
    const [showCopied, setShowCopied] = useState(false);

    useEffect(() => {
      if (
        message.citations &&
        message.citations.length > 0 &&
        message.role === "assistant"
      ) {
        // Use citations from API response
        setCitationSource(
          message.citations.map((citation) => ({
            name: citation.source_file,
            content: citation.content_snippet,
            url: citation.source_file,
            parentFolder: citation.source_file,
            ...citation,
          }))
        );
      } else if (message.role === "assistant") {
        const parsedSources = parseSourcesFromContent(message.content);
        setCitationSource(parsedSources);
      } else {
        setCitationSource([]);
      }

      setCitationMessage(message.content);
    }, [message.citations, message.content, message.role, isStreaming]);

    useEffect(() => {
      if (isStreaming) return;
      const messageElement = document.querySelector(
        `[data-message-id="${messageIndex}"]`
      );
      if (!messageElement) return;

      const walker = document.createTreeWalker(
        messageElement,
        NodeFilter.SHOW_TEXT
      );

      const textNodes: Text[] = [];
      let node;
      while ((node = walker.nextNode())) {
        textNodes.push(node as Text);
      }

      textNodes.forEach((textNode) => {
        const text = textNode.textContent || "";
        if (text.match(/\[Ref\s+\d+\]/i)) {
          const parent = textNode.parentNode;
          if (parent && parent.nodeType === Node.ELEMENT_NODE) {
            const html = text.replace(/\[Ref\s+(\d+)\]/gi, (_match, number) => {
              return `<span class="citation-link cursor-pointer text-blue-600 hover:text-blue-800 font-medium" data-citation="${number}" style="cursor: pointer; color: #2563eb; font-weight: 500;">[${number}]</span>`;
            });

            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = html;

            while (tempDiv.firstChild) {
              parent.insertBefore(tempDiv.firstChild, textNode);
            }
            parent.removeChild(textNode);
          }
        }
      });

      const handleCitationClickEvent = (event: Event) => {
        const target = event.target as HTMLElement;
        if (target.classList.contains("citation-link")) {
          event.preventDefault();
          event.stopPropagation();
          const citationNumber = parseInt(
            target.getAttribute("data-citation") || "0"
          );
          if (citationNumber > 0) {
            handleCitationClick(citationNumber);
          }
        }
      };

      messageElement.addEventListener("click", handleCitationClickEvent);

      return () => {
        messageElement.removeEventListener("click", handleCitationClickEvent);
      };
    }, [citationMessage, message.content, messageIndex, handleCitationClick, isStreaming]);

    const handleCopy = async () => {
      try {
        const messageElement = document.querySelector(
          `[data-message-id="${messageIndex}"]`
        ) as HTMLElement | null;
        if (!messageElement) {
          console.error("Message element not found");
          return;
        }
        const clone = messageElement.cloneNode(true) as HTMLElement;
        clone
          .querySelectorAll("button, .code-block-header")
          .forEach((el) => el.remove());

        const htmlContent = clone.innerHTML;
        const plainText = clone.innerText;

        const clipboardItem = new ClipboardItem({
          "text/html": new Blob([htmlContent], { type: "text/html" }),
          "text/plain": new Blob([plainText], { type: "text/plain" }),
        });

        await navigator.clipboard.write([clipboardItem]);

        setShowCopied(true);
        setTimeout(() => setShowCopied(false), COPY_TOOLTIP_DURATION);
      } catch (err) {
        console.error("Failed to copy formatted text:", err);
      }
    };

    return (
      <div
        className={`group px-4 ${
          message.role === "assistant"
            ? "text-gray-800 dark:text-gray-100"
            : "text-gray-800 dark:text-gray-100"
        }`}
        style={{ overflowWrap: "anywhere" }}
      >
        <div
          className={`m-auto flex items-start gap-4 p-3 text-base md:max-w-4xl md:gap-6 md:py-4 ${
            message.role === "assistant" ? "flex-row" : "flex-row-reverse"
          }`}
        >
          {message.role === "assistant" && (
            <div className="hidden md:flex flex-shrink-0 justify-start items-start pt-2">
              <div className="w-8 h-8 flex items-center justify-center">
                <img
                  src={aiIcon}
                  alt="CHOP GPT"
                  className="w-8 h-8 object-contain"
                />
              </div>
            </div>
          )}
          <div className="prose mt-[-2px] dark:prose-invert font-secondary flex-1 min-w-0">
            {message.role === "user" ? (
              <>
                {/* File container with horizontal scroll - right aligned */}
                {(message.selectedFiles && message.selectedFiles.length > 0) ||
                (message.uploads && message.uploads.length > 0) ? (
                  <div className="mb-3 flex items-start gap-4 md:gap-6 flex-row-reverse min-w-0">
                    {/* Spacer to match avatar alignment */}
                    <div className="hidden opacity-[0] md:flex flex-shrink-0 justify-start items-start pt-2">
                      <div className="font-primary rounded-full bg-primary-400 flex items-center justify-center">
                        <ProfileIcon />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                      {message.selectedFiles && <div className="relative">
                        <FileAttachmentWrapper fileList={message.selectedFiles?.map((file, index) => ({ id: `${file.name}-${index}-${file.size}`, file, showRemove: false }))} />
                      </div>}
                      {message.uploads && <div className="relative">
                        <FileAttachmentWrapper uploadList={message.uploads?.map((upload, index) => ({ id: `upload-${upload.id}-${index}`, upload, showRemove: false }))} />
                      </div>}
                    </div>
                  </div>
                ) : null}
                <div className="flex items-start gap-4 md:gap-6 flex-row-reverse">
                  {/* User avatar */}
                  <div className="hidden md:flex flex-shrink-0 justify-start items-start pt-2">
                    <div className="font-primary rounded-full bg-primary-400 flex items-center justify-center">
                      <ProfileIcon/>
                    </div>
                  </div>
                  <div className="max-w-[calc(100%-3rem)]">
                    <div
                      className="prose bg-white dark:bg-[#181818] py-3 px-4 whitespace-pre-wrap dark:prose-invert border border-border_chat rounded-xl text-primary-500 font-secondary inline-block"
                      style={{ maxWidth: "none", width: "auto" }}
                    >
                      {message.content}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                {message.reasoning && message.reasoning.length > 0 && (
                  <div className="mb-4">
                    <Accordion
                      type="single"
                      collapsible
                      // defaultValue={isStreaming ? "reasoning" : undefined}
                    >
                      <AccordionItem value="reasoning">
                        <AccordionTrigger className="py-0 px-0">
                          <div className="flex items-center gap-2">
                            <span className="text-base text-gray-700 font-semibold font-secondary">
                              Reasoning
                            </span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <MemoizedReactMarkdown
                            className="text-gray-500 max-w-none"
                            remarkPlugins={[remarkGfm, remarkMath]}
                            rehypePlugins={[rehypeKatex]}
                          >
                            {message.reasoning}
                          </MemoizedReactMarkdown>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  </div>
                )}

                <div data-message-id={messageIndex}>
                  {message.hasError ? <div className="border border-pink-200 border-[2px] rounded-xl px-4 py-3 text-[#c0165c] bg-pink-100">{message.content}</div> : <AssistantMessage
                    message={citationMessage || message.content}
                  />}
                </div>
                {!isStreaming && (
                  <div className="flex flex-row my-4">
                    {message.sessionId && message.id && (
                      <MessageFeedbackButton
                        messageFeedback={message.feedback}
                        sessionId={message.sessionId}
                        messageId={message.id}
                        conversationType={message.conversationType}
                      />
                    )}

                    <div className="relative flex items-center">
                      <div
                        className="w-8 h-8 rounded-full flex items-center bg-primary-50 justify-center hover:bg-[#e8effa] text-icon-text-100 mr-2 cursor-pointer"
                        onClick={handleCopy}
                        role="button"
                        aria-label="Copy message"
                      >
                        <CopyIcon size={14} />
                      </div>
                      {showCopied && (
                        <span className="absolute left-8 -translate-y-1/2 bg-gray-800 text-white text-xs rounded px-2 py-1 shadow-md whitespace-nowrap z-50">
                          Copied!
                        </span>
                      )}
                    </div>

                    {/* Citation button */}
                    <div className="ml-2">
                      <CitationButton
                        onClick={() => {
                          if (sourcesOpen) {
                            onCloseSources?.();
                          } else if (citationSource?.length > 0 && onOpenSources) {
                            onOpenSources(citationSource);
                          }
                        }}
                        hasSources={citationSource?.length > 0}
                        isActive={sourcesOpen}
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <RightSideModal
          open={open}
          reference={reference}
          loading={isLoading}
          onClose={close}
        />
      </div>
    );
  }
);

ChatMessage.displayName = "ChatMessage";
